import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Search, ArrowUp, ArrowDown, ArrowUpDown, Loader2, Plus, Flag, Send, History, FileText,
} from 'lucide-react';
import { useClaims, useClaimStats, type Claim, type ClaimStatus, deriveClaimFlags } from '@/api/claims';
import { useSubmitClaims } from '@/api/edi';
import { useSettings } from '@/api/settings';
import { MarkSubmittedDialog } from './MarkSubmittedDialog';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/FormField';
import { StatusBadge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import {
    Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader, DialogFooter,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
type SortKey = 'claimNo' | 'serviceDate' | 'client' | 'payer' | 'amount' | 'status';
type SortDir = 'asc' | 'desc';

type StatusTab = 'All' | ClaimStatus;

const STATUS_TABS: StatusTab[] = ['Ready', 'Submitted', 'Partial', 'Paid', 'Denied', 'Voided', 'All'];

const PAGE_SIZE = 25;

// Billing list defaults to the last 90 days of service. A practice accumulates
// claims for years; loading every historical row to render the Ready tab is a
// round-trip tax on every biller for rows nobody wants to see. Users who need
// older claims can widen the range (planned follow-up); adjusters typically
// work claims within a few weeks of DOS.
const DEFAULT_LOOKBACK_DAYS = 90;

function defaultDateFrom(): string {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    d.setUTCDate(d.getUTCDate() - DEFAULT_LOOKBACK_DAYS);
    return d.toISOString().slice(0, 10);
}

function fmtMoney(n: number): string {
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function fmtDate(iso: string | null): string {
    if (!iso) return '';
    return String(iso).slice(0, 10);
}

export function BillingPage() {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_billing'));
    const canSubmit = useAuthStore((s) => s.hasPermission('submit_claims'));
    const navigate = useNavigate();

    const { data: stats } = useClaimStats();
    const { data: settings } = useSettings();
    const submitM = useSubmitClaims();

    const [tab, setTab] = useState<StatusTab>('Ready');
    const [search, setSearch] = useState('');
    const [payerFilter, setPayerFilter] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('serviceDate');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [page, setPage] = useState(1);
    const [dateFrom] = useState(() => defaultDateFrom());

    // Claims tab is insurance-only. Private-pay (bill_to='Client') claims
    // live under /billing/invoices as Unbilled rows until bundled onto an
    // invoice, so filtering them out here also keeps the stats + payer list
    // in sync with what's visible. Status is pushed down to the server so
    // switching tabs fetches only the rows for the active bucket.
    const { data: claims, isLoading, isError, error } = useClaims({
        billTo: 'Payer',
        dateFrom,
        status: tab === 'All' ? undefined : tab,
    });

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [markSubmittedOpen, setMarkSubmittedOpen] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{ ok: number; fail: number; errors: string[] } | null>(null);
    // Tracks the bulk-submit batch as a single operation. submitM.isPending
    // only reflects the most recent in-flight call when we fire several in
    // parallel, so we keep our own flag for the dialog/button disabled state.
    const [batchSubmitting, setBatchSubmitting] = useState(false);
    const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);

    const payerOptions = useMemo(() => {
        if (!claims) return [] as string[];
        const set = new Set<string>();
        for (const c of claims) if (c.payer) set.add(c.payer);
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [claims]);

    const filtered = useMemo(() => {
        if (!claims) return [] as Claim[];
        const q = search.trim().toLowerCase();
        return claims.filter((c) => {
            if (tab !== 'All' && c.status !== tab) return false;
            if (payerFilter && c.payer !== payerFilter) return false;
            if (q) {
                const hay = [c.claimNo, c.client, c.payer, c.serviceCode, c.staff].join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [claims, tab, search, payerFilter]);

    const sorted = useMemo(() => {
        const rows = [...filtered];
        rows.sort((a, b) => {
            let cmp = 0;
            switch (sortKey) {
                case 'claimNo':     cmp = (a.claimNo || '').localeCompare(b.claimNo || ''); break;
                case 'serviceDate': cmp = (a.serviceDate || '').localeCompare(b.serviceDate || ''); break;
                case 'client':      cmp = (a.client || '').localeCompare(b.client || ''); break;
                case 'payer':       cmp = (a.payer || '').localeCompare(b.payer || ''); break;
                case 'amount':      cmp = a.amount - b.amount; break;
                case 'status':      cmp = (a.status || '').localeCompare(b.status || ''); break;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [filtered, sortKey, sortDir]);

    const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageRows  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    function toggleSort(key: SortKey) {
        if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortKey(key); setSortDir(key === 'amount' || key === 'serviceDate' ? 'desc' : 'asc'); }
    }

    function countFor(s: StatusTab): number {
        if (!stats) return 0;
        if (s === 'All') return stats.total;
        return stats[s.toLowerCase() as keyof typeof stats] ?? 0;
    }

    // Selection is only meaningful for Ready claims. Clear it any time the
    // visible set changes so users can't accidentally submit something scrolled
    // off-screen — matches the "filter, then act" model elsewhere in the app.
    useEffect(() => {
        setSelected(new Set());
    }, [tab, search, payerFilter]);

    // Private-pay invoices (billTo='Client') can't be submitted to 837P —
    // exclude from the bulk-select set so the checkbox is disabled.
    const selectableIds = useMemo(
        () => new Set(sorted.filter((c) => c.status === 'Ready' && c.billTo !== 'Client').map((c) => c.id)),
        [sorted],
    );
    const selectableCount = selectableIds.size;
    const selectedCount = useMemo(() => {
        let n = 0;
        for (const id of selected) if (selectableIds.has(id)) n++;
        return n;
    }, [selected, selectableIds]);
    const allSelected = selectableCount > 0 && selectedCount === selectableCount;

    // Split the selection by submission_method so the bulk-action rail can
    // show the right button (and refuse to act on a mixed selection). Claims
    // snapshot submission_method from the payer at creation, so a biller's
    // typical workflow is "filter by payer, select all, act". Pre-migration
    // rows have a null submission_method — treat them as Electronic so the
    // existing 837P path continues to handle them.
    const selectedClaims = useMemo(() => {
        if (!claims) return [] as Claim[];
        return claims.filter((c) => selected.has(c.id) && selectableIds.has(c.id));
    }, [claims, selected, selectableIds]);
    const selectedElectronic = useMemo(
        () => selectedClaims.filter((c) => c.submissionMethod !== 'Manual'),
        [selectedClaims],
    );
    const selectedManual = useMemo(
        () => selectedClaims.filter((c) => c.submissionMethod === 'Manual'),
        [selectedClaims],
    );
    const mixedSelection = selectedElectronic.length > 0 && selectedManual.length > 0;

    function toggleRow(id: string, checked: boolean) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }
    function toggleAll(checked: boolean) {
        if (checked) setSelected(new Set(selectableIds));
        else setSelected(new Set());
    }

    // Preflight only runs against the Electronic-payer subset. Manual claims
    // don't go through 837P so their missing-fields list would be misleading
    // in this dialog; the row-level Flag icon still surfaces issues before
    // the biller hits "Mark as Submitted".
    const preflightIssues = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const c of selectedElectronic) {
            const issues: string[] = [];
            if (!c.payer) issues.push('missing payer');
            if (!c.payerId) issues.push('missing payer ID');
            if (!c.serviceDate) issues.push('missing DOS');
            if (!c.serviceCode) issues.push('missing service code');
            if (!c.units) issues.push('missing units');
            if (!c.amount) issues.push('missing billed amount');
            if (c.frequency !== 'original' && !c.originalClaimNumber) {
                issues.push(`${c.frequency} requires original claim #`);
            }
            if (issues.length) map.set(c.id, issues);
        }
        return map;
    }, [selectedElectronic]);

    const blockingIds = Array.from(preflightIssues.keys());
    const submittableIds = selectedElectronic
        .filter((c) => !preflightIssues.has(c.id))
        .map((c) => c.id);

    async function runSubmission() {
        if (!submittableIds.length) return;
        const isTest = Boolean(settings?.billing?.oaTestMode);
        const result = { ok: 0, fail: 0, errors: [] as string[] };

        // Server submits one claim per 837P envelope (one submission row,
        // one audit trail each — see submitClaims.js). Sending them serially
        // means N round-trips of network + SFTP latency in series, which is
        // painful for a typical biller submitting 20–30 Ready claims at once.
        // We parallelize with a small in-flight cap so 5 uploads run at a
        // time. Concurrency is capped (not unbounded) because the SFTP upload
        // is the real bottleneck downstream and we don't want to surprise
        // Office Ally with N simultaneous connections.
        const CONCURRENCY = 5;
        setBatchSubmitting(true);
        setBatchProgress({ done: 0, total: submittableIds.length });
        let doneCount = 0;

        const queue = submittableIds.slice();
        async function worker(): Promise<void> {
            while (queue.length > 0) {
                const claimId = queue.shift()!;
                try {
                    await submitM.mutateAsync({ claimIds: [claimId], isTest });
                    result.ok += 1;
                } catch (e) {
                    result.fail += 1;
                    const msg = extractErrorMessage(e, 'Submission failed');
                    const claim = claims?.find((c) => c.id === claimId);
                    result.errors.push(`${claim?.claimNo ?? claimId}: ${msg}`);
                }
                doneCount += 1;
                setBatchProgress({ done: doneCount, total: submittableIds.length });
            }
        }
        await Promise.all(
            Array.from({ length: Math.min(CONCURRENCY, submittableIds.length) }, () => worker()),
        );

        setBatchSubmitting(false);
        setBatchProgress(null);
        setSubmitStatus(result);
        setSelected(new Set());
        setConfirmOpen(false);
    }

    return (
        <>
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-text-subtle">
                    Claims are auto-created when sessions are finalized. Manually add or edit only when necessary.
                </p>
                <div className="flex items-center gap-2">
                    {canSubmit && (
                        <Button variant="ghost" size="sm" onClick={() => navigate('/billing/submissions')}>
                            <History className="w-3.5 h-3.5" /> Submissions
                        </Button>
                    )}
                    {canEdit && (
                        <Button variant="primary" size="sm" onClick={() => navigate('/billing/new')}>
                            <Plus className="w-3.5 h-3.5" /> New claim
                        </Button>
                    )}
                </div>
            </div>

            <Card className="p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Filter claims by status" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {STATUS_TABS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                role="tab"
                                aria-selected={tab === s}
                                onClick={() => { setTab(s); setPage(1); }}
                                className={cn(
                                    'px-3 py-1 text-xs rounded transition-colors inline-flex items-center gap-1.5',
                                    tab === s
                                        ? 'bg-surface text-text shadow-xs font-medium'
                                        : 'text-text-muted hover:text-text',
                                )}
                            >
                                {s}
                                {stats && (
                                    <span className="text-2xs tabular-nums text-text-subtle">
                                        {countFor(s)}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    <div className="relative flex-1 min-w-[220px] max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-subtle" />
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Claim #, client, payer, code…"
                            aria-label="Search claims"
                            className="pl-8 h-8 py-1"
                        />
                    </div>
                    <select
                        value={payerFilter}
                        onChange={(e) => { setPayerFilter(e.target.value); setPage(1); }}
                        aria-label="Filter by payer"
                        className="h-8 px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                    >
                        <option value="">All payers</option>
                        {payerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <div className="ml-auto flex items-center gap-2">
                        {canSubmit && selectedCount > 0 && mixedSelection && (
                            <span
                                className="text-xs text-warning-text"
                                title="Select only Electronic payers or only Manual payers at a time."
                            >
                                Mixed selection — pick one payer type
                            </span>
                        )}
                        {canSubmit && selectedElectronic.length > 0 && !mixedSelection && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => { setSubmitStatus(null); setConfirmOpen(true); }}
                                disabled={batchSubmitting}
                            >
                                <Send className="w-3.5 h-3.5" /> Submit {selectedElectronic.length}
                            </Button>
                        )}
                        {canSubmit && selectedManual.length > 0 && !mixedSelection && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setMarkSubmittedOpen(true)}
                            >
                                <Send className="w-3.5 h-3.5" /> Mark {selectedManual.length} submitted
                            </Button>
                        )}
                        <div className="text-xs text-text-subtle tabular-nums">
                            {sorted.length} {sorted.length === 1 ? 'claim' : 'claims'}
                        </div>
                    </div>
                </div>
                {submitStatus && (
                    <div
                        className={cn(
                            'px-3 py-2 text-xs border-b border-border',
                            submitStatus.fail ? 'bg-warning-soft text-warning-text' : 'bg-success-soft text-success-text',
                        )}
                    >
                        {submitStatus.ok > 0 && (
                            <span>Submitted {submitStatus.ok} claim{submitStatus.ok === 1 ? '' : 's'}.</span>
                        )}
                        {submitStatus.fail > 0 && (
                            <span className="ml-2">
                                {submitStatus.fail} failed: {submitStatus.errors.slice(0, 3).join('; ')}
                                {submitStatus.errors.length > 3 ? '…' : ''}
                            </span>
                        )}
                    </div>
                )}

                {isLoading ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    {canSubmit && <th className="pl-3 pr-0 py-2 w-7" />}
                                    <th className="px-3 py-2 font-bold">Claim #</th>
                                    <th className="px-3 py-2 font-bold">DOS</th>
                                    <th className="px-3 py-2 font-bold">Client</th>
                                    <th className="px-3 py-2 font-bold">Payer</th>
                                    <th className="px-3 py-2 font-bold">Code</th>
                                    <th className="px-3 py-2 font-bold text-right">Units</th>
                                    <th className="px-3 py-2 font-bold text-right">Billed</th>
                                    <th className="px-3 py-2 font-bold text-right">Paid</th>
                                    <th className="px-3 py-2 font-bold text-right">PR</th>
                                    <th className="px-3 py-2 font-bold">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="border-b border-border last:border-b-0">
                                        {canSubmit && <td className="pl-3 pr-0 py-2 w-7"><Skeleton className="h-4 w-4" /></td>}
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-20" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-16" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-28" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-24" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-14" /></td>
                                        <td className="px-3 py-2 text-right"><Skeleton className="h-3 w-8 ml-auto" /></td>
                                        <td className="px-3 py-2 text-right"><Skeleton className="h-3 w-14 ml-auto" /></td>
                                        <td className="px-3 py-2 text-right"><Skeleton className="h-3 w-14 ml-auto" /></td>
                                        <td className="px-3 py-2 text-right"><Skeleton className="h-3 w-10 ml-auto" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-5 w-16 rounded-full" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load claims</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="p-12 flex flex-col items-center gap-3 text-center">
                        <FileText className="w-8 h-8 text-text-subtle" aria-hidden />
                        {claims && claims.length > 0 ? (
                            <>
                                <p className="text-sm font-medium text-text">No claims match the current filters</p>
                                <p className="text-xs text-text-subtle">Try adjusting the status tab, search, or payer filter.</p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-medium text-text">No claims yet</p>
                                <p className="text-xs text-text-subtle">Claims are created automatically when sessions are finalized.</p>
                                {canEdit && (
                                    <Button variant="primary" size="sm" onClick={() => navigate('/billing/new')}>
                                        <Plus className="w-3.5 h-3.5" /> Create a claim manually
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                        {canSubmit && (
                                            <th className="pl-3 pr-0 py-2 w-7">
                                                <input
                                                    type="checkbox"
                                                    aria-label="Select all Ready claims on this page"
                                                    checked={allSelected}
                                                    disabled={selectableCount === 0}
                                                    onChange={(e) => toggleAll(e.target.checked)}
                                                    className="h-4 w-4 align-middle"
                                                />
                                            </th>
                                        )}
                                        <SortableTh label="Claim #"       sortKey="claimNo"     current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                        <SortableTh label="DOS"           sortKey="serviceDate" current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                        <SortableTh label="Client"        sortKey="client"      current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                        <SortableTh label="Payer"         sortKey="payer"       current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                        <th className="px-3 py-2 font-bold">Code</th>
                                        <th className="px-3 py-2 font-bold text-right">Units</th>
                                        <SortableTh label="Billed"        sortKey="amount"      current={sortKey} dir={sortDir} onToggle={toggleSort} align="right" />
                                        <th className="px-3 py-2 font-bold text-right">Paid</th>
                                        <th className="px-3 py-2 font-bold text-right">PR</th>
                                        <SortableTh label="Status"        sortKey="status"      current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((c) => {
                                        const flags = c.flags.length ? c.flags : deriveClaimFlags(c);
                                        return (
                                            <tr
                                                key={c.id}
                                                onClick={(e) => {
                                                    if (e.defaultPrevented) return;
                                                    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                                                    navigate(`/billing/${c.id}`);
                                                }}
                                                className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors cursor-pointer"
                                            >
                                                {canSubmit && (
                                                    <td
                                                        className="pl-3 pr-0 py-2 w-7"
                                                        onClick={(e) => { e.stopPropagation(); }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            aria-label={`Select claim ${c.claimNo}`}
                                                            disabled={!selectableIds.has(c.id)}
                                                            checked={selected.has(c.id)}
                                                            onChange={(e) => toggleRow(c.id, e.target.checked)}
                                                            className="h-4 w-4 align-middle"
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <Link to={`/billing/${c.id}`} className="font-medium hover:underline">
                                                        {c.claimNo}
                                                    </Link>
                                                    {c.primaryClaimId && (
                                                        <span className="ml-1.5 text-2xs uppercase tracking-wide text-text-subtle">2°</span>
                                                    )}
                                                    {c.billTo === 'Client' && (
                                                        <span className="ml-1.5 px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wider bg-surface-muted rounded-sm text-text-muted">
                                                            Invoice
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-text-muted whitespace-nowrap">{fmtDate(c.serviceDate)}</td>
                                                <td className="px-3 py-2">{c.client}</td>
                                                <td className="px-3 py-2 text-text-muted">{c.billTo === 'Client' ? 'Private-Pay' : (c.payer || '—')}</td>
                                                <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                                                    {c.serviceCode}
                                                    {c.mod1 ? ` ${c.mod1}` : ''}
                                                    {c.mod2 ? `/${c.mod2}` : ''}
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">{c.units || ''}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(c.amount)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums text-text-muted">
                                                    {c.paidAmount ? fmtMoney(c.paidAmount) : ''}
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums text-text-muted">
                                                    {c.prBalance ? fmtMoney(c.prBalance) : ''}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-1.5">
                                                        <StatusBadge status={c.status} />
                                                        {c.billTo === 'Payer' && c.submissionMethod && (
                                                            <span
                                                                className="inline-flex items-center px-1.5 py-0.5 text-2xs font-medium uppercase tracking-wide bg-surface-muted text-text-muted rounded-sm"
                                                                title={`Claim will be submitted ${c.submissionMethod.toLowerCase()}ly`}
                                                            >
                                                                {c.submissionMethod === 'Manual' ? 'Manual' : 'EDI'}
                                                            </span>
                                                        )}
                                                        {flags.length > 0 && (
                                                            <span
                                                                title={flags.join('\n')}
                                                                aria-label={`${flags.length} validation flag${flags.length === 1 ? '' : 's'}`}
                                                                className="inline-flex items-center text-warning-text"
                                                            >
                                                                <Flag className="w-3.5 h-3.5" />
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {pageCount > 1 && (
                            <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs text-text-muted">
                                <div>Page {page} of {pageCount}</div>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={page === 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={page >= pageCount}
                                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Submit {submittableIds.length} claim{submittableIds.length === 1 ? '' : 's'}?</DialogTitle>
                        <DialogDescription>
                            {settings?.billing?.oaTestMode
                                ? 'Test-mode is on — filenames will include OATEST and the clearinghouse will validate without adjudicating.'
                                : 'Live submission. Claims will be uploaded to Office Ally for adjudication.'}
                        </DialogDescription>
                    </DialogHeader>

                    {blockingIds.length > 0 && (
                        <div className="border border-warning bg-warning-soft rounded-md p-3 text-xs">
                            <p className="font-medium text-warning-text mb-1">
                                {blockingIds.length} selected claim{blockingIds.length === 1 ? ' is' : 's are'} missing required fields and will be skipped:
                            </p>
                            <ul className="list-disc pl-5 space-y-0.5">
                                {blockingIds.slice(0, 8).map((id) => {
                                    const claim = claims?.find((c) => c.id === id);
                                    return (
                                        <li key={id}>
                                            <span className="font-medium">{claim?.claimNo ?? id}</span>:{' '}
                                            {preflightIssues.get(id)?.join(', ')}
                                        </li>
                                    );
                                })}
                                {blockingIds.length > 8 && <li>…and {blockingIds.length - 8} more</li>}
                            </ul>
                        </div>
                    )}

                    {batchSubmitting && (
                        <div className="flex items-center gap-2 text-sm text-text-subtle">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {batchProgress
                                ? `Submitting ${batchProgress.done} of ${batchProgress.total}…`
                                : 'Submitting…'}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)} disabled={batchSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={runSubmission}
                            disabled={submittableIds.length === 0 || batchSubmitting}
                        >
                            <Send className="w-3.5 h-3.5" />
                            Submit {submittableIds.length}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <MarkSubmittedDialog
                open={markSubmittedOpen}
                claims={selectedManual}
                onOpenChange={setMarkSubmittedOpen}
                onSuccess={() => setSelected(new Set())}
            />
        </>
    );
}

function SortableTh({
    label, sortKey, current, dir, onToggle, align,
}: {
    label: string;
    sortKey: SortKey;
    current: SortKey;
    dir: SortDir;
    onToggle: (key: SortKey) => void;
    align?: 'right';
}) {
    const active = current === sortKey;
    const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
    return (
        <th className={cn('px-3 py-2 font-bold', align === 'right' && 'text-right')}>
            <button
                type="button"
                onClick={() => onToggle(sortKey)}
                className={cn(
                    'inline-flex items-center gap-1 uppercase tracking-wider text-2xs',
                    active ? 'text-text' : 'text-text-subtle hover:text-text',
                )}
                aria-label={`Sort by ${label}${active ? ` (${dir === 'asc' ? 'ascending' : 'descending'})` : ''}`}
            >
                {label}
                <Icon className="w-3 h-3" />
            </button>
        </th>
    );
}
